import { useEventSubscription } from '@pwc/event-hub-ui';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { selectAnalysisContent } from '../../features/analyses/analysisSlice';
import { categoryPathRoute } from '../../features/divestitureDiagnostic/routes';
import { getCurrentKfa } from '../../features/hypotheses/hypothesisSlice';
import { getCurrentInsight } from '../../features/insights/insightsServiceNew';
import { getProject } from '../../features/project/projectSlice';
import { previewPageSelector } from '../redux/reports/reportsSelectors';

export const DEFAULT_TITLE = 'Junction (Legacy)';

const makeTitle = (getStr) => (args) => {
  const firstPart = getStr(args);
  if (firstPart) {
    return `${firstPart} | ${DEFAULT_TITLE}`;
  }

  return DEFAULT_TITLE;
};

const makeProject = (getStr) => (args) => {
  const contentTypeTitle = getStr(args);
  const projectTitle = args.projectName;
  if (contentTypeTitle && projectTitle) {
    return `${contentTypeTitle} - ${projectTitle}`;
  }
  return undefined;
};

const makeProjectTitle = (getStr) => (args) =>
  makeTitle(makeProject(getStr))(args);

const ROUTES = [
  // Projects
  { path: '/projects', title: makeTitle(() => 'Projects') },
  {
    path: '/projects/create-project',
    title: makeTitle(() => 'Create a new project'),
  },
  {
    path: '/projects/:projectId',
    title: makeTitle(({ projectName }) => projectName),
  },
  {
    path: '/projects/:projectId/topics/draft',
    title: makeTitle(({ projectName }) => projectName),
  },
  {
    path: '/projects/:projectId/topics/shared',
    title: makeTitle(({ projectName }) => projectName),
  },
  {
    path: '/projects/:projectId/topic/:contentId/view',
    title: makeTitle(({ projectName }) => projectName),
  },
  {
    path: '/projects/:projectId/topic/:contentId/edit',
    title: makeTitle(({ projectName }) => projectName),
  },
  // Insights
  {
    path: '/projects/:projectId/insights',
    title: makeProjectTitle(() => `Insights`),
  },
  {
    path: '/projects/:projectId/insights/create',
    title: makeProjectTitle(() => `Add new Insight`),
  },
  {
    path: '/projects/:projectId/insights/:insightId/view',
    title: makeProjectTitle(({ insightTitle }) => `${insightTitle}`),
  },
  {
    path: '/projects/:projectId/insights/:insightId/edit',
    title: makeProjectTitle(() => 'Edit Insight'),
  },

  // Key Focus Areas
  {
    path: '/projects/:projectId/key-focus-areas',
    title: makeProjectTitle(() => `Key Focus Areas`),
  },
  {
    path: '/projects/:projectId/key-focus-areas/create',
    title: makeProjectTitle(() => `Add new Key Focus Area`),
  },
  {
    path: '/projects/:projectId/key-focus-areas/:hypothesisId/view',
    title: makeProjectTitle(({ hypothesisTitle }) =>
      hypothesisTitle ? `${hypothesisTitle}` : undefined
    ),
  },
  {
    path: '/projects/:projectId/key-focus-areas/:hypothesisId/edit',
    title: makeProjectTitle(() => 'Edit Key Focus Area'),
  },

  // Analyses
  {
    path: '/projects/:projectId/analyses',
    title: makeProjectTitle(() => `Analyses`),
  },
  {
    path: '/projects/:projectId/analyses/create',
    title: makeProjectTitle(() => `Add new Analysis`),
  },
  {
    path: '/projects/:projectId/analyses/create/library-items',
    title: makeProjectTitle(() => `Add data - Add new Analysis`),
  },
  {
    path: '/projects/:projectId/analyses/create/layout',
    title: makeProjectTitle(() => `Add layout - Add new Analysis`),
  },
  {
    path: '/projects/:projectId/analyses/add-type',
    title: makeProjectTitle(() => `Add Analysis`),
  },
  {
    path: '/projects/:projectId/analyses/add-from-template',
    title: makeProjectTitle(() => `Add Analysis from template`),
  },
  {
    path: '/projects/:projectId/analyses/quality-of-earnings',
    title: makeProjectTitle(() => `Quality of Earnings`),
  },
  {
    path: '/projects/:projectId/template-analyses/:contentThreadId/view/:version',
    title: makeProjectTitle(
      ({ structuredAnalysisTitle }) => structuredAnalysisTitle
    ),
  },
  {
    path: '/projects/:projectId/template-analyses/:contentThreadId/edit',
    title: makeProjectTitle(
      ({ structuredAnalysisTitle }) => `Edit ${structuredAnalysisTitle}`
    ),
  },
  {
    path: '/projects/:projectId/template-analyses/:contentThreadId/report-preview/:version',
    title: makeProjectTitle(
      ({ structuredAnalysisTitle }) =>
        `${structuredAnalysisTitle} - Report Preview`
    ),
  },
  {
    path: '/projects/:projectId/analyses/quality-of-earnings/edit',
    title: makeProjectTitle(() => `Edit Quality of Earnings`),
  },
  {
    path: '/projects/:projectId/analyses/net-working-capital',
    title: makeProjectTitle(() => `Net Working Capital`),
  },
  {
    path: '/projects/:projectId/analyses/net-working-capital/edit',
    title: makeProjectTitle(() => `Edit Net Working Capital`),
  },
  {
    path: '/projects/:projectId/analyses/debt-debt-like',
    title: makeProjectTitle(() => `Debt and Debt Like`),
  },
  {
    path: '/projects/:projectId/analyses/debt-debt-like/edit',
    title: makeProjectTitle(() => `Edit Debt and Debt Like`),
  },
  {
    path: '/projects/:projectId/analyses/:analysisId/view',
    title: makeProjectTitle(({ analysisTitle }) => `${analysisTitle}`),
  },
  {
    path: '/projects/:projectId/analyses/:analysisId/edit',
    title: makeProjectTitle(() => 'Edit Analysis'),
  },

  // Settings
  {
    path: '/projects/:projectId/settings',
    title: makeProjectTitle(() => `Settings`),
  },

  // Reports
  {
    path: '/projects/:projectId/reports',
    title: makeProjectTitle(() => `Reports`),
  },
  {
    path: '/projects/:projectId/reports/create-report',
    title: makeProjectTitle(() => `Report Builder`),
  },
  {
    path: '/projects/:projectId/reports/create-report/filter',
    title: makeProjectTitle(() => `Report Builder Filtered Results`),
  },
  {
    path: '/projects/:projectId/reports/create-report/preview/report',
    title: makeProjectTitle(() => `Report Preview`),
  },
  {
    path: '/projects/:projectId/reports/create-report/preview/cover',
    title: makeProjectTitle(() => `Cover - Report Preview`),
  },
  {
    path: '/projects/:projectId/reports/create-report/preview/tile/:tileId',
    title: makeProjectTitle(({ tileTitle }) => `${tileTitle} - Report Preview`),
  },

  // Notifications
  {
    path: '/projects/:projectId/divestiture-diagnostic/survey/:sectionPath/:questionId',
    title: makeProjectTitle(() => 'Diagnostic Questionnaire'),
  },
  {
    path: '/projects/:projectId/divestiture-diagnostic/survey/responses',
    title: makeProjectTitle(() => 'Diagnostic Responses'),
  },
  {
    path: categoryPathRoute,
    title: makeProjectTitle(() => 'Diagnostic Results'),
  },

  // Notifications
  {
    path: '/projects/:projectId/notifications',
    title: makeProjectTitle(() => 'Notifications'),
  },
  {
    path: '/projects/:projectId/notifications/mentions',
    title: makeProjectTitle(() => 'Mentions - Notifications'),
  },
  {
    path: '/projects/:projectId/notifications/approval-requests',
    title: makeProjectTitle(() => 'Approval Requests - Notifications'),
  },
  {
    path: '/projects/:projectId/notifications/project-activity',
    title: makeProjectTitle(() => 'Project Activity - Notifications'),
  },

  // Legal
  {
    path: '/terms-and-conditions',
    title: makeTitle(() => 'Terms & Conditions'),
  },
  {
    path: '/policies/privacy-statement',
    title: makeTitle(() => 'Privacy Statement'),
  },
  {
    path: '/policies/cookie-statement',
    title: makeTitle(() => 'Cookie Statement'),
  },
  {
    path: '/policies/terms-of-use',
    title: makeTitle(() => 'Terms Of Use'),
  },
  {
    path: '/projects/:projectId/project-disclaimer',
    title: makeProjectTitle(() => 'Project Disclaimer'),
  },

  // Error
  {
    path: '/notfound',
    title: makeTitle(() => 'Not Found'),
  },
  {
    path: '/unauthorized',
    title: makeTitle(() => 'Unauthorized'),
  },
  {
    path: '/unauthenticated',
    title: makeTitle(() => 'Unauthenticated'),
  },
];

const useMatchedRoute = (route) => ({
  ...route,
  match: useRouteMatch(route.path),
});

const useMatchedRoutes = () => ROUTES.map(useMatchedRoute);

export const useExactMatchedRoute = () =>
  useMatchedRoutes().filter((r) => (r.match || {}).isExact)[0];

export const getTopic = (state) =>
  state?.projectDomain?.selectedItem?.details?.read?.data?.content?.title;
export const useCustomRouteNames = () => {
  const project = useSelector(getProject);
  const kfa = useSelector(getCurrentKfa);
  const analysis = useSelector(selectAnalysisContent);
  const insight = useSelector(getCurrentInsight);
  const tile = useSelector(previewPageSelector);
  const topicTitle = useSelector(getTopic);
  const [structuredAnalysisTitle, setStructuredAnalysisTitle] = useState('');

  useEventSubscription('structured-analysis-title-fetched', (event) => {
    if (event.data?.title) {
      setStructuredAnalysisTitle(event.data?.title);
    }
  });
  return {
    projectName: project.name,
    hypothesisTitle: kfa && kfa.content && kfa.content.title,
    analysisTitle: analysis && analysis.title,
    insightTitle: insight.title || 'Loading insight',
    tileTitle: tile.title,
    topicTitle: topicTitle || '',
    structuredAnalysisTitle,
  };
};
